import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DARKER_TEXT } from 'resources/constants/colors';
import { IMAGES_URL } from 'resources/constants/urls';
import styled from 'styled-components';
import NoProfileSVG from 'resources/img/no-pic-therapist.png';
import { getStatusText } from 'utils/text';
import { getStatusColor } from 'utils';
import { getDisplayTime } from 'utils/time';
import { add } from 'date-fns';
import { Box, Rating, Typography, Button, TextField, CircularProgress } from '@mui/material';
import { appointmentsAPI } from 'resources/api';

const Container = styled.div`
    margin: 0;
    margin-bottom: 10px;
    background: #fff;
    box-shadow: 0 2px 12px rgba(0,0,0,0.07);
    border-radius: 20px;

    a {
        padding: 10px;
        display: flex;
        gap: 10px;
        align-items: center;
        color: ${DARKER_TEXT};
        text-decoration: none;
    }

    .image-container {
        width: 66px;
        height: 66px;
        overflow: hidden;
        border-radius: 12px;
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    .information {
        flex: 1;
        min-height: 50px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .texts {
            color: ${DARKER_TEXT};
            h4 {
                font-size: 16px;
                margin: 0;
                user-select: none;
                margin-bottom: 5px;
            }
            p {
                font-weight: 600;
                margin: 0;
            }
        }
    }
`;

const AppointmentCard = ({ app, isTherapist }) => {
    const [rating, setRating] = useState(null);
    const [comment, setComment] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const canRate = !isTherapist && app.status === 'COMPLETED' && !app.rating && !submitted;

    const handleSubmitReview = async () => {
        if (!rating) return;
        setLoading(true);
        try {
            await appointmentsAPI.addReview(app.id, { rating, comment });
            setSubmitted(true);
        } catch (e) {
            // silently fail — user can retry
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container>
            <Link to={`/appointment/${app.roomId}`}>
                <div className="image-container">
                    <img
                        src={
                            app.profileImg
                                ? `${IMAGES_URL}${app.profileImg}`
                                : NoProfileSVG
                        }
                        alt={`perfil de ${app.name} ${app.lastName}`}
                    />
                </div>
                <div className="information">
                    <div className="texts">
                        <h4>{`${app.title ?? ''} ${app.name} ${app.lastName}`}</h4>
                        <p>{getDisplayTime(app.date)} - {getDisplayTime(add(new Date(app.date), { minutes: 50 }))}</p>
                        <p style={{ color: getStatusColor(app) }}>{getStatusText(app)}</p>
                    </div>
                </div>
            </Link>
            {canRate && (
                <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" fontWeight={600}>¿Cómo fue tu sesión?</Typography>
                    <Rating
                        value={rating}
                        onChange={(_, val) => setRating(val)}
                        size="large"
                    />
                    <TextField
                        size="small"
                        multiline
                        rows={2}
                        placeholder="Comentario opcional..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        disabled={!rating || loading}
                        onClick={handleSubmitReview}
                        sx={{ borderRadius: '20px', alignSelf: 'flex-end' }}
                    >
                        {loading ? <CircularProgress size={18} color="inherit" /> : 'Enviar calificación'}
                    </Button>
                </Box>
            )}
            {submitted && (
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                    <Typography variant="body2" color="success.main" fontWeight={600}>¡Gracias por tu calificación! ⭐</Typography>
                </Box>
            )}
            {!isTherapist && app.rating && (
                <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating value={app.rating} readOnly size="small" />
                    <Typography variant="caption" color="text.secondary">Tu calificación</Typography>
                </Box>
            )}
        </Container>
    );
};

export default AppointmentCard;
